import { produce } from 'immer';
import { message } from 'antd';
import {
  mutateRemoveBox,
  mutateSelectBox,
  boxBounding,
  alignedBoxValues,
  mutateMoveBoxInfinite,
  mutateMoveBoxesBatchInfinite,
  boxesToMoveInfinite,
  getAlignedBoxTargetValue,
  isASelectionBox,
  isAVariableBox,
  mutateVariablesBoxes,
  mutateAddBoxes,
  mutateMoveBoxFromGroup,
  shouldMeasureGroupBox,
  mutateAlignBoxBy,
  mutateFitGroupBoxToBoxes,
  mutateScaleGroupBox,
  isATitleBox,
  planBoxLayerMove,
} from '../utils/boxes';
import { pushCopy } from '../utils/copies';
import { ItemTypes, uuid, viewerTypes } from '../constants/constants';
import { variableLockKeysInBox } from '../collab/variableRefs';
import {
  columnsLockHolder,
  containerLockHolder,
  configuratorHolder,
  anyRemoteLockHolder,
  previewLockHolder,
  previewVariableLockHolder,
} from '../collab/structureLocks';
import { getBoxById, getContainerByColumnId } from './lookups';
import { withEphemeralMutation } from '../undo/undoHistory';

import { gridUnit } from '../constants/gridConfig';
import { getColumnAvailableWidth } from '../utils/columns';
import { mutateTakeOutItem } from '../utils/variableOrderArray';
import {
  mutateSearchAndLaunchTrigger,
  mutateRemoveTriggersWithTriggerId,
} from '../utils/triggers';
import { mutateMoveBoxesIndex, mutateTitlesIndexes } from '../utils/titles';

export const boxesActions = (set, get) => ({
  boxHideInFormSwitcher: (id) => {
    set(
      produce((draft) => {
        const box = draft.boxes.find((b) => b.id === id);
        if (!box.hideInForm) {
          box.hideInForm = true;
        } else {
          delete box.hideInForm;
        }
      }),
      false,
      `boxHideInFormSwitcher ${id}`
    );
  },
  boxVariableSwitcher: (id) => {
    set(
      produce((draft) => {
        const box = draft.boxes.find((b) => b.id === id);
        const getType = (type) => {
          switch (type) {
            case ItemTypes.IMG:
              return ItemTypes.IMG_VARIABLE;
            case ItemTypes.TABLE:
              return ItemTypes.TABLE_VARIABLE;
            case ItemTypes.FILE_PDF:
              return ItemTypes.FILE_PDF_VARIABLE;
            case ItemTypes.TEXT:
              return ItemTypes.TEXT_VARIABLE;
            case ItemTypes.IMG_VARIABLE:
              return ItemTypes.IMG;
            case ItemTypes.TABLE_VARIABLE:
              return ItemTypes.TABLE;
            case ItemTypes.FILE_PDF_VARIABLE:
              return ItemTypes.FILE_PDF;
            case ItemTypes.TEXT_VARIABLE:
              return ItemTypes.TEXT;
            default:
              return null;
          }
        };
        const variableType = getType(box.type);
        box.type = variableType;
        if (isAVariableBox(variableType)) {
          const newBox = mutateVariablesBoxes(draft, null, [box])[0];
          box.name = newBox.name;
        } else {
          if (box.name) delete box.name;
          if (box.remark) delete box.remark;
          if (box.predefinedValues) delete box.predefinedValues;
          if (box.hideInForm) delete box.hideInForm;
          mutateRemoveTriggersWithTriggerId({ triggerId: box.id, draft });
          mutateTakeOutItem(draft, box.id);
        }
      }),
      false,
      `boxVariableSwitcher ${id}`
    );
  },
  updateBox: (id, fn, options = {}, callback) => {
    // Mutation de mesure/layout (rendu, sélection) : exécutée sous garde
    // ephemeral → ignorée par la pile undo, et sans envoi collab à elle seule
    // (elle part avec la modification suivante, cf. structureSync).
    if (options.ephemeral) {
      withEphemeralMutation(() =>
        get().updateBox(id, fn, { ...options, ephemeral: false })
      );
      if (callback) callback();
      return;
    }
    set(
      produce((draft) => {
        const maskBoxes = draft.masks.map((m) => m.boxes).flat();
        const boxInMask = maskBoxes.find((b) => b.id === id);
        if (boxInMask) {
          fn(boxInMask, true);
          options.actionNameSuffix = options.actionNameSuffix || 'from_mask';
          // Sync: mask title1 (template edit) → injected title in document via linkedContainerId
          if (isATitleBox(boxInMask.type)) {
            const parentMask = draft.masks.find((m) => m.boxes.some((b) => b.id === id));
            if (parentMask?.chapterSeparation) {
              const linkedTitleBox = draft.boxes.find((b) => b.linkedToMaskId === parentMask.id && isATitleBox(b.type));
              if (linkedTitleBox) {
                linkedTitleBox.content = { ...linkedTitleBox.content, ...boxInMask.content };
              }
            }
          }
        } else {
          const box = draft.boxes.find((b) => b.id === id);
          // Box déjà supprimée (ex. sauvegarde au démontage d'un bloc dont le
          // container vient d'être retiré) : rien à écrire.
          if (!box) return;
          if (!box.onlyForm) {
            const column = draft.columns.find((c) => c.id === box.columnId);
            const columnWidth = getColumnAvailableWidth({
              columnId: column.id,
              columns: draft.columns,
              containers: draft.containers,
              rowContainers: draft.rowContainers,
              landscape: draft.landscape,
              marginLeft: draft.configuration.margins.left * gridUnit,
              marginRight: draft.configuration.margins.right * gridUnit,
            });
            fn(box, column, columnWidth);
          } else {
            fn(box);
          }
          // Sync title content between the two linked containers (mask ↔ linked title)
          if (isATitleBox(box.type)) {
            const boxContainer = draft.containers.find((c) => c.columnsIds.includes(box.columnId));
            if (boxContainer?.linkedContainerId) {
              const linkedContainer = draft.containers.find((c) => c.id === boxContainer.linkedContainerId);
              if (linkedContainer) {
                // Find the title box in the paired container (across all its columns)
                const pairedTitleBox = draft.boxes.find(
                  (b) => linkedContainer.columnsIds.includes(b.columnId) && isATitleBox(b.type)
                );
                if (pairedTitleBox) {
                  pairedTitleBox.content = { ...pairedTitleBox.content, ...box.content };
                }
              }
            }
          }
          if (draft.currentDocument.type !== 'template') {
            mutateSearchAndLaunchTrigger({ triggerId: box.id, draft });
          }
          if (!options?.shouldNotMeasureGroupBox)
            shouldMeasureGroupBox(boxInMask ? maskBoxes : draft.boxes, box);
        }
      }),
      false,
      `updateBox${id}_${
        options.actionNameSuffix ? '_' + options.actionNameSuffix : ''
      }`
    );
    if (callback) callback();
  },
  // Avancer (step 1) / reculer (step -1) d'un plan, cf. planBoxLayerMove.
  moveBoxLayer: (id, step) => {
    const state = get();
    const box = getBoxById(state.boxes, state.masks, id);
    if (!box) return;
    const mask = state.masks.find((m) => m.boxes.some((b) => b.id === id));
    const changes = planBoxLayerMove({
      boxes: mask ? mask.boxes : state.boxes,
      boxId: id,
      step,
    });
    if (changes.length === 0) return;
    const layerIds = box.type === ItemTypes.GROUP_BOXES ? box.boxIds : [id];
    // Reculer au plancher fait monter les voisines : le diff collab porte la
    // box entière, et écraserait celle qu'un co-éditeur est en train d'éditer.
    if (changes.some((change) => !layerIds.includes(change.id))) {
      const holder = columnsLockHolder(state, [box.columnId]);
      if (holder) {
        message.warning(`Impossible de reculer : ${holder.name} édite ce bloc`);
        return;
      }
    }
    set(
      produce((draft) => {
        const boxes = mask
          ? draft.masks.find((m) => m.id === mask.id).boxes
          : draft.boxes;
        changes.forEach((change) => {
          const changed = boxes.find((b) => b.id === change.id);
          if (changed) changed.zIndex = change.zIndex;
        });
      }),
      false,
      `moveBoxLayer${id}`
    );
  },
  batchUpdateBox: (updates) => {
    set(
      produce((draft) => {
        const maskBoxes = draft.masks.map((m) => m.boxes).flat();
        updates.forEach(({ id, fn }) => {
          const boxInMask = maskBoxes.find((b) => b.id === id);
          if (boxInMask) {
            fn(boxInMask, true);
          } else {
            const box = draft.boxes.find((b) => b.id === id);
            if (!box) return;
            if (!box.onlyForm) {
              const column = draft.columns.find((c) => c.id === box.columnId);
              const columnWidth = getColumnAvailableWidth({
                columnId: column.id,
                columns: draft.columns,
                containers: draft.containers,
                rowContainers: draft.rowContainers,
                landscape: draft.landscape,
                marginLeft: draft.configuration.margins.left * gridUnit,
                marginRight: draft.configuration.margins.right * gridUnit,
              });
              fn(box, column, columnWidth);
            } else {
              fn(box);
            }
            if (draft.currentDocument.type !== 'template') {
              mutateSearchAndLaunchTrigger({ triggerId: box.id, draft });
            }
            shouldMeasureGroupBox(draft.boxes, box);
          }
        });
      }),
      false,
      'batchUpdateBox'
    );
  },
  // Presse-papiers multi-slots : on garde les MAX_COPIES derniers éléments
  // copiés, du plus récent au plus ancien (cf. pushCopy).
  addCopy: ({ sourceId, item }) =>
    set(
      (state) => ({ copies: pushCopy(state.copies, { sourceId, item }) }),
      false,
      'addCopy'
    ),
  // Retire un élément du presse-papiers : les slots sont limités (MAX_COPIES),
  // et rien ne permettait de faire de la place autrement qu'en copiant autre
  // chose jusqu'à ce que l'ancien tombe de la liste.
  removeCopy: (sourceId) =>
    set(
      (state) => ({
        copies: state.copies.filter((entry) => entry.sourceId !== sourceId),
      }),
      false,
      'removeCopy'
    ),
  getBox: (id) =>
    [
      ...get().boxes,
      ...get()
        .masks.map((m) => m.boxes)
        .flat(),
    ].find((box) => box.id === id),
  moveBoxInfinite: (left, top, boxId) => {
    set(
      produce((draft) => {
        mutateMoveBoxInfinite(draft, left, top, boxId);
      }),
      false,
      'move Box'
    );
  },
  removeBox: (id) => {
    set(
      produce((state) => {
        const boxes = [
          ...state.boxes,
          ...state.masks.map((m) => m.boxes).flat(),
        ];
        const selectedBox =
          state.selectedBoxId &&
          boxes.find((b) => b.id === state.selectedBoxId);
        const maskId =
          selectedBox &&
          state.maskEditing?.length > 0 &&
          state.maskEditing.includes(selectedBox.maskId)
            ? selectedBox.maskId
            : null;
        if (selectedBox?.type === ItemTypes.GROUP_BOXES) {
          const boxesInGroup = boxes.filter((b) =>
            selectedBox.boxIds.includes(b.id)
          );
          boxesInGroup.forEach((b) => {
            mutateRemoveBox(state, b.id, maskId);
          });
        }
        mutateRemoveBox(state, id, maskId);
      }),
      false,
      'remove Box'
    );
  },
  updateIndexTitle: () => {
    set(
      produce((draft) => {
        mutateTitlesIndexes(draft);
      }),
      false,
      `updateIndexTitle`
    );
  },
  // Hors numérotation : le drapeau tient sur la box, mais il décale les numéros
  // de tous les titres qui suivent → recalcul de l'index dans la foulée.
  toggleTitleNumbering: (id) => {
    set(
      produce((draft) => {
        // Les titres d'un masque de séparation de chapitre sont aussi dans
        // draft.boxes : on cherche d'abord là, sinon le masque garderait une
        // copie désynchronisée du document.
        const box = draft.boxes.find((b) => b.id === id);
        const mask = box
          ? null
          : draft.masks.find((m) => m.boxes.some((b) => b.id === id));
        const target = box ?? mask?.boxes.find((b) => b.id === id);
        if (!target) return;
        target.unnumbered = !target.unnumbered;
        mutateTitlesIndexes(draft, mask ? mask.id : null);
      }),
      false,
      `toggleTitleNumbering${id}`
    );
  },
  moveBoxesIndex: ({
    oldDragTitle,
    aboveDropTitle,
    dropPosition,
    boxesInColumn,
    forDelete,
  }) => {
    // Collab : déplacer un titre réordonne tout un bloc de containers et
    // recale la position des box en aval → bloqué si un co-éditeur édite quoi
    // que ce soit (sinon sa box éditée saute en plein milieu)
    const remote = anyRemoteLockHolder(get());
    if (remote) {
      message.warning(`Déplacement impossible : ${remote.name} édite le document`);
      return false;
    }
    let res = '';
    set(
      produce((draft) => {
        const succes = mutateMoveBoxesIndex(draft, {
          oldDragTitle,
          aboveDropTitle,
          dropPosition,
          boxesInColumn,
          forDelete,
        });
        res = succes;
        if (succes === false) {
          return false;
        }
      }),
      false,
      `moveBoxesIndex`
    );
    return res;
  },
  handleSelectBox: ({ box, group, fromViewer }) => {
    // Collab : box verrouillée par un co-éditeur → sélection refusée
    const locks = get().boxLocks;
    const userId = get().user?.id;
    // Un co-éditeur configure le document → toute édition de box refusée
    const configurator = configuratorHolder(get());
    if (configurator) {
      message.warning(`${configurator.name} configure le document`);
      return;
    }
    const lock = locks[box.id];
    if (lock && lock.id !== userId) {
      message.warning(`Bloc en cours d'édition par ${lock.name}`);
      return;
    }
    // Container exclusif : une autre box du même container est éditée par un
    // co-éditeur → sélection refusée
    if (box.columnId) {
      const container = getContainerByColumnId(get().containers, box.columnId);
      const containerHolder =
        container && containerLockHolder(get(), container.id);
      if (containerHolder) {
        message.warning(
          `Conteneur verrouillé : ${containerHolder.name} édite un bloc dedans`
        );
        return;
      }
    }
    // Section en cours d'aperçu par un co-éditeur → sélection refusée
    const previewHolder = previewLockHolder(get(), box.id);
    if (previewHolder) {
      message.warning(`Section en aperçu par ${previewHolder.name}`);
      return;
    }
    // Variable embarquée dans le contenu en cours d'édition au formulaire
    // par un co-éditeur → sélection refusée (sinon écritures croisées sur le
    // contenu via le remplacement de variables)
    const lockedVariableKey = variableLockKeysInBox(box).find(
      (key) => locks[key] && locks[key].id !== userId
    );
    if (lockedVariableKey) {
      message.warning(
        `Variable en cours d'édition par ${locks[lockedVariableKey].name}`
      );
      return;
    }
    const isFromFormViewer = fromViewer === viewerTypes.VARIABLES;
    set(
      produce((draft) => {
        draft.eventManagerEnabled = true;
        mutateSelectBox(draft, box, group);
        if (!isAVariableBox(box.type) && draft.boxVariableSelectedId) {
          draft.boxVariableSelectedId = null;
        }
        // Dans FormViewer : mise à jour immédiate pour que la bordure du fieldset s'affiche tout de suite.
        // Dans InfinitePage : boxVariableSelectedId est différé au prochain frame (ci-dessous).
        if (isAVariableBox(box.type) && isFromFormViewer) {
          draft.boxVariableSelectedId = box.id;
        }
      }),
      false,
      `Select Box${group ? ' in group' : ''} `
    );
    if (isAVariableBox(box.type) && !isFromFormViewer) {
      const boxId = box.id;
      requestAnimationFrame(() => {
        set(
          produce((draft) => {
            draft.boxVariableSelectedId = boxId;
          }),
          false,
          'selectBoxVariable deferred'
        );
      });
    }
  },
  handleUnSelectBox: (from = 'anonymous') => {
    if (get().selectedBoxId) {
      set(
        produce((draft) => {
          draft.eventManagerEnabled = false;
          draft.selectedBoxId = null;
          draft.boxIsMovable = false;
          draft.groupSelection = [];
        }),
        false,
        `unselectBox from ${from}`
      );
    }
  },
  handleBoxVariableSelectedId: (boxId, shouldScroll = false) => {
    if (boxId) {
      const locks = get().boxLocks;
      const userId = get().user?.id;
      // Un co-éditeur configure le document → édition de variable refusée
      const configurator = configuratorHolder(get());
      if (configurator) {
        message.warning(`${configurator.name} configure le document`);
        return;
      }
      // Variable déjà éditée au formulaire par un co-éditeur
      const lock = locks[boxId];
      if (lock && lock.id !== userId) {
        message.warning(`Variable en cours d'édition par ${lock.name}`);
        return;
      }
      // Variable embarquée dans une box verrouillée par un co-éditeur
      // (boxId vaut 'custom.<id>' pour une variable texte, l'id de la
      // variable box sinon — même clé que dans les entités VARIABLE)
      const blockingId = Object.keys(locks).find((lockedId) => {
        if (locks[lockedId].id === userId) return false;
        const lockedBox = get().boxes.find((b) => b.id === lockedId);
        return lockedBox && variableLockKeysInBox(lockedBox).includes(boxId);
      });
      if (blockingId) {
        message.warning(
          `Variable utilisée dans un bloc édité par ${locks[blockingId].name}`
        );
        return;
      }
      // Variable liée à une section en cours d'aperçu par un co-éditeur
      const previewVarHolder = previewVariableLockHolder(get(), boxId);
      if (previewVarHolder) {
        message.warning(`Section en aperçu par ${previewVarHolder.name}`);
        return;
      }
    }
    set(
      produce((draft) => {
        // Optimisation : trouver la box une seule fois au lieu de deux find()
        const box = draft.boxes.find((b) => b.id === boxId);
        const isACustomTextVariable = !box;
        draft.boxVariableSelectedId = boxId;
        // L'occurrence est reposée juste après par le clic sur la variable
        // (VariableDecorator) ; toute autre sélection la périme.
        draft.variableOccurrenceSelected = null;
        // Flag pour indiquer si on doit scroller dans FormViewer
        // Utilisé uniquement quand on clique depuis FormItemsArray (tableau de gauche)
        draft.shouldScrollToVariableInFormViewer = shouldScroll;
        if (draft.currentDocument.type === 'template')
          draft.selectedBoxId = null;
        if (
          !isACustomTextVariable &&
          (!boxId || !isASelectionBox(box.type)) &&
          draft.currentDocument.type !== 'template'
        )
          draft.selectedBoxId = boxId;
      }),
      false,
      `${!!boxId ? 'selectBoxVariable' : 'unselectBoxVariable'} id ${boxId}`
    );
  },
  selectVariableOccurrence: (occurrence) => {
    set(
      produce((draft) => {
        draft.variableOccurrenceSelected = occurrence ?? null;
      }),
      false,
      `selectVariableOccurrence ${occurrence?.variableKey ?? ''}`
    );
  },
  setShouldScrollToVariableInFormViewer: (shouldScroll) => {
    set(
      produce((draft) => {
        draft.shouldScrollToVariableInFormViewer = shouldScroll;
      }),
      false,
      `setShouldScrollToVariableInFormViewer ${shouldScroll}`
    );
  },
  openTableConfigDrawer: (boxId) => {
    set(
      produce((draft) => {
        draft.tableConfigBoxId = boxId;
      }),
      false,
      `openTableConfigDrawer ${boxId}`
    );
  },
  closeTableConfigDrawer: () => {
    set(
      produce((draft) => {
        draft.tableConfigBoxId = null;
      }),
      false,
      'closeTableConfigDrawer'
    );
  },
  // Drawer « Plus de paramètres » d'une case à cocher / d'un choix. Même raison
  // que le drawer des tableaux de garder l'état dans le store : il est ouvert
  // depuis une section du panel de droite, montée par la box elle-même.
  openVariableFormDrawer: (boxId) => {
    set(
      produce((draft) => {
        draft.variableFormDrawerBoxId = boxId;
      }),
      false,
      `openVariableFormDrawer ${boxId}`
    );
  },
  closeVariableFormDrawer: () => {
    set(
      produce((draft) => {
        draft.variableFormDrawerBoxId = null;
      }),
      false,
      'closeVariableFormDrawer'
    );
  },
  // Ouverture/fermeture de la modale d'édition d'organigramme. L'état vit dans le
  // store et non dans OrgChart : le clic qui ouvre sélectionne aussi la box, ce
  // qui échange Dragger <-> Resizer (BoxWrapper) et démonte le composant.
  openOrgChartModal: (boxId) => {
    set(
      produce((draft) => {
        draft.orgChartModalBoxId = boxId;
      }),
      false,
      `openOrgChartModal ${boxId}`
    );
  },
  closeOrgChartModal: () => {
    set(
      produce((draft) => {
        draft.orgChartModalBoxId = null;
      }),
      false,
      'closeOrgChartModal'
    );
  },
  setSlider: (obj) => {
    set(
      produce((draft) => {
        let directionChange = false;
        if (obj.direction && obj.direction !== draft.slider.direction) {
          directionChange = true;
        }
        Object.keys(obj).forEach((k) => {
          draft.slider[k] = obj[k];
        });
        if (directionChange) {
          draft.slider.boxes = boxesToMoveInfinite(
            draft.boxes,
            draft.slider.from.value
          ).map((b) => b.id);
        }
      }),
      false,
      `Set Slider`
    );
  },
  moveBoxesInfinite: (moveObject) => {
    set(
      produce((draft) => {
        mutateMoveBoxesBatchInfinite(draft, moveObject);
      }),
      false,
      'moveBoxes'
    );
  },
  alignBoxesDrawMode: (side) => {
    set(
      produce((draft) => {
        const allBoxes = [
          ...draft.boxes,
          ...draft.masks.map((m) => m.boxes).flat(),
        ];
        const selectedBox = allBoxes.find((b) => b.id === draft.selectedBoxId);
        // Groupe sélectionné : on aligne ses membres entre eux, sur le cadre du
        // groupe (qui épouse leur emprise) — grouper puis aligner ne proposait
        // que l'alignement du groupe sur les marges de la page.
        const boxesInGroup =
          selectedBox?.type === ItemTypes.GROUP_BOXES
            ? allBoxes.filter((b) => selectedBox.boxIds?.includes(b.id))
            : null;
        // Sinon multi-sélection : les box du groupe de sélection s'alignent sur
        // la box maîtresse.
        const boxes =
          boxesInGroup ??
          allBoxes.filter((b) => draft.groupSelection.includes(b.id));
        const boundingSelectedBox = boxBounding(selectedBox);
        let newSideValue;
        newSideValue = boundingSelectedBox[side];
        boxes.forEach((box) => {
          mutateAlignBoxBy({
            side,
            box,
            targetValue: newSideValue,
            boxes: allBoxes,
          });
        });
        if (boxesInGroup) mutateFitGroupBoxToBoxes(selectedBox, boxesInGroup);
      }),
      false,
      `Align boxes on ${side}`
    );
  },
  distributeBoxesDrawMode: (side) => {
    set(
      produce((draft) => {
        const allBoxes = [
          ...draft.boxes,
          ...draft.masks.map((m) => m.boxes).flat(),
        ];
        const selectedBox = allBoxes.find((b) => b.id === draft.selectedBoxId);
        // Groupe sélectionné : la répartition porte sur ses membres (les box du
        // groupe), pas sur le cadre du groupe qui n'a rien à répartir.
        const boxesInGroup =
          selectedBox?.type === ItemTypes.GROUP_BOXES
            ? allBoxes.filter((b) => selectedBox.boxIds?.includes(b.id))
            : null;
        // Copie : l'algorithme retire les extrêmes de la liste avec splice, et
        // le recalage du cadre du groupe a besoin de tous les membres.
        const boxesToDistribute =
          (boxesInGroup && [...boxesInGroup]) ??
          allBoxes.filter(
            (b) =>
              draft.groupSelection.includes(b.id) || b.id === draft.selectedBoxId
          );

        const lowestSide = side === 'horizontal' ? 'left' : 'top';
        const highestSide = side === 'horizontal' ? 'right' : 'bottom';
        const lowest = Math.min(
          ...boxesToDistribute.map((b) => boxBounding(b)[lowestSide])
        );
        const highest = Math.max(
          ...boxesToDistribute.map((b) => boxBounding(b)[highestSide])
        );
        boxesToDistribute.sort((boxA, boxB) => {
          return ~~boxA.zIndex - ~~boxB.zIndex;
        });

        const lowestIndex = boxesToDistribute.findIndex(
          (box) => boxBounding(box)[lowestSide] === lowest
        );
        const highestIndex = boxesToDistribute.findIndex(
          (box) => boxBounding(box)[highestSide] === highest
        );
        let lowestHighest = boxBounding(boxesToDistribute[lowestIndex])[
          highestSide
        ];
        let highestLowest = boxBounding(boxesToDistribute[highestIndex])[
          lowestSide
        ];
        if (lowestHighest > highestLowest) {
          const temp = highestLowest;
          highestLowest = lowestHighest;
          lowestHighest = temp;
        }
        boxesToDistribute.splice(lowestIndex, 1);
        if (highestIndex !== lowestIndex) {
          boxesToDistribute.splice(
            boxesToDistribute.findIndex(
              (box) => boxBounding(box)[highestSide] === highest
            ),
            1
          );
        }
        boxesToDistribute.sort((boxA, boxB) => {
          return boxBounding(boxA)[lowestSide] - boxBounding(boxB)[lowestSide];
        });
        const spaceTaken = boxesToDistribute.reduce(
          (acc, box) =>
            acc +
            (side === 'horizontal'
              ? boxBounding(box).width
              : boxBounding(box).height),
          0
        );
        const totalEmptySpace = highestLowest - lowestHighest - spaceTaken;
        const spaceBetweenBoxes =
          totalEmptySpace / (boxesToDistribute.length + 1);

        boxesToDistribute.forEach((box, idx) => {
          const centerPoint =
            lowestHighest +
            spaceBetweenBoxes * (idx + 1) +
            boxesToDistribute
              .slice(0, idx)
              .reduce(
                (acc, b) =>
                  acc +
                  (side === 'horizontal'
                    ? boxBounding(b).width
                    : boxBounding(b).height),
                0
              ) +
            (side === 'horizontal'
              ? boxBounding(box).width
              : boxBounding(box).height) /
              2;

          mutateAlignBoxBy({
            side,
            box,
            targetValue: centerPoint,
            boxes: allBoxes,
          });
        });
        if (boxesInGroup) mutateFitGroupBoxToBoxes(selectedBox, boxesInGroup);
      }),
      false,
      `Distribute boxes on ${side}`
    );
  },
  alignBoxAbsolutDrawMode: (side) => {
    set(
      produce((draft) => {
        const boxes = [
          ...draft.boxes,
          ...draft.masks.map((m) => m.boxes).flat(),
        ];
        const selectedBox = [
          ...draft.boxes,
          ...draft.masks.map((m) => m.boxes).flat(),
        ].find((b) => b.id === draft.selectedBoxId);
        const targetValue = getAlignedBoxTargetValue({
          draft,
          selectedBox,
          side,
        });
        const { propertyToAlign, value } = alignedBoxValues({
          side,
          box: selectedBox,
          targetValue,
        });
        const oldLeft = selectedBox.left;
        const oldTop = selectedBox.top;
        selectedBox[propertyToAlign] += value;
        if (selectedBox.type === ItemTypes.GROUP_BOXES) {
          mutateMoveBoxFromGroup({
            box: selectedBox,
            boxes,
            newLeft: selectedBox.left,
            newTop: selectedBox.top,
            oldLeft,
            oldTop,
          });
        }
        selectedBox.alignToken = uuid();
      }),
      false,
      `Absolut Align box at ${side} margin`
    );
  },
  alignBoxDefault: (positions) => {
    set(
      produce(
        (draft) => {
          const selectedBox = draft.boxes.find(
            (b) => b.id === draft.selectedBoxId
          );
          selectedBox.positions = { ...selectedBox.positions, ...positions };
        },
        false,
        `Default Align box at vertical : ${positions.vertical} and horizontal : ${positions.horizontal}`
      )
    );
  },
  setBoxMovable: (value) => {
    set(
      produce((state) => {
        state.boxIsMovable = value;
      }),
      false,
      `setBoxMovable to ${value}`
    );
  },
  addBox: (box, callback) => {
    set(
      produce((draft) => {
        mutateAddBoxes({ draft, newBoxes: [box] });
        if (callback) {
          callback(box, draft);
        }
      }),
      false,
      `add box`
    );
  },
  addGroupBox: (groupBox) => {
    set(
      produce((draft) => {
        const boxes = draft.boxes.filter((b) => groupBox.boxIds.includes(b.id));
        boxes.forEach((b) => {
          b.groupTop = groupBox.top;
          b.originalLeft = b.left;
          b.originalTop = b.top;
          b.originalRotation = b.rotation;
        });
        const maskId =
          groupBox &&
          draft.maskEditing?.length > 0 &&
          draft.maskEditing.includes(groupBox.maskId)
            ? groupBox.maskId
            : null;
        mutateAddBoxes({
          draft,
          newBoxes: [groupBox],
          maskId,
        });
        draft.groupSelection = [];
        draft.selectedBoxId = groupBox.id;
      }),
      false,
      `add group box`
    );
  },
  // Mise à l'échelle d'un groupe : le cadre et ses membres bougent dans la même
  // mutation — une seule entrée d'annulation, une seule diffusion aux autres
  // éditeurs, et jamais d'état intermédiaire où le cadre a grandi sans son
  // contenu.
  scaleGroupBox: (groupBoxId, scale) => {
    set(
      produce((draft) => {
        const allBoxes = [
          ...draft.boxes,
          ...draft.masks.flatMap((m) => m.boxes ?? []),
        ];
        const groupBox = allBoxes.find((b) => b.id === groupBoxId);
        if (!groupBox || groupBox.type !== ItemTypes.GROUP_BOXES) return;
        mutateScaleGroupBox({
          groupBox,
          boxesInGroup: allBoxes.filter((b) => groupBox.boxIds?.includes(b.id)),
          scale,
        });
      }),
      false,
      `scale group box`
    );
  },
  ungroupBoxes: (groupBox) => {
    set(
      produce((draft) => {
        const boxes = draft.boxes.filter((b) => groupBox.boxIds.includes(b.id));
        boxes.forEach((b) => {
          delete b.groupTop;
          delete b.originalLeft;
          delete b.originalTop;
          delete b.originalRotation;
        });
        const maskId =
          groupBox &&
          draft.maskEditing?.length > 0 &&
          draft.maskEditing.includes(groupBox.maskId)
            ? groupBox.maskId
            : null;
        mutateRemoveBox(draft, groupBox.id, maskId);
        draft.groupSelection = [];
        draft.selectedBoxId = null;
      }),
      false,
      `ungroup boxes`
    );
  },
});
